import React, { useMemo } from 'react';
import Select, {
  components, PlaceholderProps, GroupBase, OptionProps, ValueContainerProps, MultiValue,
} from 'react-select';
import { FrontendFilterValue } from '../../types/filters.type';
import useFilterStore from '../streetview/filterStore';
import FilterDescription from '../streetview/filterDescription';
import { reactElementIsType } from '../../utils/helpers';

const { ValueContainer, Placeholder, Option } = components;

// creates custom option component that has checkbox
function CustomOption(props: Readonly<OptionProps<FrontendFilterValue, true>>) {
  const { isSelected, data, label } = props;

  return (
    <div>
      <Option {...props}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => null}
          id={`${label}-${data.id}`}
          className="accent-primary"
        />
        <label htmlFor={`${label}-${data.id}`} className="px-2">{data.displayName}</label>
      </Option>
    </div>
  );
}
interface CustomValueContainerProps extends ValueContainerProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>> {
  children: React.ReactNode;
}

const createPlaceholderText = (customProps: CustomValueContainerProps): string => {
  const { selectProps } = customProps;

  const isTyping = selectProps.inputValue && selectProps.inputValue.length > 0;

  const allSelected = (Array.isArray(selectProps.value) && selectProps.value.some((opt: FrontendFilterValue) => opt.id === 'all'))
  || (Array.isArray(selectProps.value) && selectProps.value.length === selectProps.options.length);
  const numberOfItems = Array.isArray(selectProps.value) ? selectProps.value.length : '';
  const placeholderText = allSelected ? 'All items selected' : `${numberOfItems} item(s) selected`;
  return isTyping || (Array.isArray(selectProps.value) && selectProps.value.length === 0) ? '' : placeholderText;
};

// creates custom value container that displays number of selected items
function CustomValueContainer({ children, ...props }: Readonly<CustomValueContainerProps>) {
  const placeholderText = createPlaceholderText({ children, ...props });
  return (
    <ValueContainer {...props}>
      <Placeholder {...props as PlaceholderProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>>}>
        {placeholderText}
      </Placeholder>
      {React.Children.map(children, (child) => (!reactElementIsType(child, Placeholder) ? child : null))}
    </ValueContainer>
  );
}

// hides the per-item chips; CustomValueContainer shows a count instead
function CustomMultiValue() {
  return null;
}

export default function MultiCheckboxDropdown({
  multiSelectOptions, label, title, labelId, description, hasAllOption, featureFlag,
}: Readonly<{
  multiSelectOptions: MultiValue<FrontendFilterValue>,
  label: string,
  title: string,
  labelId: string,
  description?: string | null,
  hasAllOption: boolean,
  featureFlag?: string | null,
}>) {
  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const setSelectedFilters = useFilterStore((state) => state.setSelectedFilters);

  const currentValues = useMemo(() => selectedFilters[title]?.[labelId] || [], [selectedFilters, title, labelId]);
  const allOption: FrontendFilterValue = { id: 'all', displayName: 'Select All' };

  const selectedOptions = useMemo(() => {
    const selected = multiSelectOptions.filter(
      (option: FrontendFilterValue) => currentValues.selectedValues?.some((value) => value.id === option.id),
    );
    if (selected.length === multiSelectOptions.length && selected.length > 0) {
    // All options are selected, so include "all"
      return [allOption, ...selected];
    }
    return selected;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiSelectOptions, currentValues]);

  const handleChange = (selectedOptionsProps: MultiValue<FrontendFilterValue>, actionMeta: { action: string; option?: FrontendFilterValue }) => {
    const allOptionId = 'all';
    const allOptions = multiSelectOptions.filter((option) => option.id);
    const selectedWithoutAll = selectedOptionsProps.filter((option) => option.id !== allOptionId);

    if (actionMeta.option?.id === allOptionId) {
      if (selectedWithoutAll.length === multiSelectOptions.length) {
        setSelectedFilters(title, labelId, label, []);
      } else {
        setSelectedFilters(title, labelId, label, allOptions);
      }
      return;
    }

    setSelectedFilters(title, labelId, label, selectedWithoutAll);
  };
  const selectOptions = hasAllOption ? [allOption, ...multiSelectOptions] : multiSelectOptions;

  return (
    <label htmlFor={`multi-select-${labelId}`} className="input-label">
      {label}
      <FilterDescription description={description} featureFlag={featureFlag} />
      <Select
        inputId={`multi-select-${labelId}`}
        isMulti
        options={selectOptions}
        getOptionValue={(option) => option.id}
        classNames={{
          control: ({ isFocused }) => `!flex !p-0 input-default ${isFocused ? 'outline-none ring-1 ring-primary border-transparent' : ''} `,
          option: ({ isSelected }) => `p-2 bg-transparent hover:text-secondary hover:hover:bg-pipbg-subtle
          ${isSelected ? 'text-primary-dark hover:bg-pipbg-subtle' : 'text-secondary'}`,
        }}
        styles={{ // keep basic default styles so not all custom
          control: () => ({}),
          option: () => ({}),
        }}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        value={selectedOptions}
        onChange={handleChange}
        components={{
          MultiValue: CustomMultiValue,
          ValueContainer: CustomValueContainer,
          Option: CustomOption,
          IndicatorSeparator: () => null,
        }}
      />
    </label>
  );
}
